import { useI18n } from 'vue-i18n'
import { defineStore } from 'pinia';
import { ApiResult, unwrapResult, isApiException } from '@/api/eOrdersApi';
import { useRequestConfig } from '@/composables/requestConfig';
import { AxiosRequestConfig, isAxiosError } from 'axios';
import * as Sentry from '@sentry/vue';
import { useGlobal } from '.';
import { showError } from '@/utils/utils';

export type ApiAdapterResult<TSuccess> =
  | { type: 'success'; data: TSuccess; response: any }
  | { type: 'not_modified' }
  | { type: 'handledError' };

export default defineStore('apiAdapter', () => {
  const { t } = useI18n()
  const requestConfig = useRequestConfig();
  const global = useGlobal();

  const defaultOptions = JSON.stringify({});

  async function invoke<TSuccess>(
      action: (config: AxiosRequestConfig<any>) => Promise<ApiResult<TSuccess, void>>,
      errorTemplate?: any,
      loader: boolean = true
  ) {
    const result = await invokeRaw(action, errorTemplate, loader);
    if (result.type === 'success') return unwrapResult(result);
  }

  async function invokeRaw<TSuccess>(
      action: (config: AxiosRequestConfig<any>) => Promise<ApiResult<TSuccess, void>>,
      errorTemplate?: string | Function,
      loader: boolean = true
  ): Promise<ApiAdapterResult<TSuccess>> {
    loader && global.setLoading(true);
    let result: ApiResult<TSuccess, void>;

    try {
      result = await action(requestConfig as AxiosRequestConfig<any>);
      if (result.type === 'success') {
        if (result.data === false)
          throw new Error('Result status is false');

        return result;
      } else {
        console.log('API result error', result);
        throw new Error();
      }
    } catch (err) {
      if ((isApiException(err) && err.status === 304) || (isAxiosError(err) && err.response?.status === 304)) {
        return { type: 'not_modified' };
      }
      
      console.error('API error', err);

      const status = isAxiosError(err) ? (err.response?.status ?? 0) : (isApiException(err) ? err.status : 0);
      
      if (status === 0 || status >= 500) {
        Sentry.captureException(err, {
          tags: { source: 'apiAdapter', httpStatus: String(status) },
        });
      }
      
      if (typeof errorTemplate === 'function') {
        errorTemplate(err);
      } else {
        showError(t(errorTemplate ?? 'error.loadingData'));
      }
      
      return { type: 'handledError' };
    } finally {
      loader && global.setLoading(false);
    }
  }

  return {
    invoke,
    invokeRaw,
    defaultOptions,
  };
});
